import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSearch } from "@/context/SearchContext";
import OpportunityCard from "@/components/OpportunityCard";
import OpportunityMap from "@/components/OpportunityMap";
import UpgradeModal from "@/components/UpgradeModal";
import { MissionProgress } from "@/components/MissionProgress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { MagnifyingGlass, Sparkle, BookmarkSimple, Broadcast, Buildings, Target, MapTrifold, ListBullets, Funnel, ShieldWarning, X, MapPin, Stack, Clock, EyeSlash, Coins, Handshake } from "@phosphor-icons/react";

const OPP_TYPES = [
  { v: "final", label: "Clients finaux", icon: Buildings },
  { v: "esn", label: "ESN & intermédiaires", icon: Handshake },
  { v: "all", label: "Tous", icon: Stack },
];

const EXAMPLES = [
  "DevOps senior, AWS, Kubernetes et Terraform autour de Lille dans un rayon de 50 km.",
  "Data Engineer confirmé, Python, Spark et Kafka sur Paris.",
  "Architecte Cloud AWS expert, sécurité et microservices, région lyonnaise.",
];

const SCORE_FILTERS = [
  { v: "0", label: "Tous les scores" },
  { v: "50", label: "50 et plus" },
  { v: "60", label: "60 et plus" },
  { v: "70", label: "70 et plus" },
  { v: "80", label: "80 et plus" },
  { v: "90", label: "90 et plus" },
];

const SENIORITY_FILTERS = [
  { v: "all", label: "Toutes séniorités" },
  { v: "Débutant", label: "Débutant" },
  { v: "Junior", label: "Junior" },
  { v: "Confirmé", label: "Confirmé" },
  { v: "Senior", label: "Senior" },
  { v: "Expert", label: "Expert" },
];

const DIST_MAX = 300;

function RoleAutocomplete({ onPick }) {
  const [q, setQ] = useState("");
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setSug([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/taxonomy/roles/search?q=${encodeURIComponent(q.trim())}`);
        setSug(data); setOpen(true);
      } catch { setSug([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const pick = (r) => { onPick(r); setQ(""); setSug([]); setOpen(false); };
  return (
    <div className="mt-3 flex items-center gap-2 text-xs text-muted flex-wrap" data-testid="role-autocomplete">
      <Stack size={13} className="text-accent" />
      <span>Ajouter un métier ciblé :</span>
      <div className="relative w-64">
        <input
          value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => sug.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="ex : DevOps, Data Engineer, Architecte…"
          data-testid="role-autocomplete-input"
          className="w-full bg-surface-2 border border-border rounded-md px-3 py-1.5 text-sm text-fg focus:outline-none focus:border-accent/40"
        />
        {open && sug.length > 0 && (
          <div className="absolute z-30 mt-1 w-72 bg-surface border border-border rounded-md shadow-xl max-h-64 overflow-y-auto" data-testid="role-autocomplete-list">
            {sug.map((r) => (
              <button key={r.code} onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                data-testid={`role-suggestion-${r.code}`}
                className="w-full text-left px-3 py-2 hover:bg-surface-2 border-b border-border/40 last:border-0">
                <div className="text-sm text-fg">{r.name_fr}</div>
                <div className="text-[11px] text-muted">{r.family_name}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DistanceGauge({ value, includeRemote, onChange, onToggleRemote }) {
  const isAll = value === "all";
  const v = isAll ? DIST_MAX : Number(value);
  return (
    <div className="surface rounded-lg p-5 mb-4" data-testid="distance-gauge">
      <div className="flex items-center justify-between mb-3">
        <div className="overline flex items-center gap-2"><MapPin size={13} className="text-accent" /> Rayon de recherche</div>
        <div className="font-mono text-sm font-bold text-accent" data-testid="distance-gauge-value">
          {isAll ? "France entière" : `${v} km`}
        </div>
      </div>
      <SliderPrimitive.Root
        className="relative flex w-full touch-none select-none items-center h-5"
        min={10} max={DIST_MAX} step={5} value={[v]}
        onValueChange={(arr) => onChange(arr[0] >= DIST_MAX ? "all" : arr[0])}
        data-testid="distance-gauge-slider" aria-label="Rayon de recherche en km">
        <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-surface-2">
          <SliderPrimitive.Range className="absolute h-full bg-accent" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full border-2 border-accent bg-bg shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 cursor-grab active:cursor-grabbing" />
      </SliderPrimitive.Root>
      <div className="flex items-center justify-between mt-2.5">
        <span className="text-[0.65rem] text-muted font-mono">10 km</span>
        <button onClick={onToggleRemote} data-testid="include-remote-toggle"
          className={`text-xs flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors ${includeRemote ? "bg-accent/12 border-accent/40 text-accent" : "border-border text-muted hover:text-fg"}`}>
          <Broadcast size={12} /> Inclure télétravail & national
        </button>
        <span className="text-[0.65rem] text-muted font-mono">France</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const orgType = user?.entitlements?.org_type || user?.organization?.org_type;
  const { query, result, view, minScore, hideInter, region, maxKm, includeRemote, relatedOnly, sortBy, oppType, tjmMin, tjmMax, tjmInclUnspec, seniority, set, reset } = useSearch();
  const [loading, setLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [mission, setMission] = useState(null);
  const searchSeq = useRef(0);
  const missionId = useRef(null);

  useEffect(() => {
    if (orgType === "FINAL_CLIENT") navigate("/app/marketplace", { replace: true });
  }, [orgType, navigate]);

  // Rafraîchit les badges de statut commercial au retour sur la liste (ex. après un envoi
  // d'email / positionnement), sans relancer la recherche IA.
  useEffect(() => {
    const ids = (result?.opportunities || []).map((o) => o.opportunity?.id).filter(Boolean);
    if (!ids.length) return;
    let cancelled = false;
    api.post("/opportunities/positioning-status", { signal_ids: ids })
      .then(({ data }) => {
        if (cancelled) return;
        const pm = data.positioning || {};
        const updated = (result.opportunities || []).map((o) => ({
          ...o, positioning: pm[o.opportunity?.id] || null,
        }));
        set({ result: { ...result, opportunities: updated } });
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async (q, opts = {}) => {
    const text = (q ?? query).trim();
    if (!text) { toast.error("Décrivez le profil de votre consultant."); return; }
    const remote = opts.includeRemote ?? includeRemote;
    const sort = opts.sortBy ?? sortBy;
    const ot = opts.oppType ?? oppType;
    const tmin = opts.tjmMin ?? tjmMin;
    const tmax = opts.tjmMax ?? tjmMax;
    const tincl = opts.tjmInclUnspec ?? tjmInclUnspec;
    set({ query: text, result: null });
    setLoading(true);
    if (loading && missionId.current) api.post(`/research/missions/${missionId.current}/cancel`).catch(() => {});
    const seq = ++searchSeq.current;
    setMission(null);
    try {
      const { data: created } = await api.post("/research/missions", {
        query: text, include_remote: remote, sort,
        opportunity_type: ot,
        tjm_min: tmin === "" || tmin == null ? null : Number(tmin),
        tjm_max: tmax === "" || tmax == null ? null : Number(tmax),
        tjm_include_unspecified: tincl,
      });
      missionId.current = created.mission_id;
      let m = null;
      while (seq === searchSeq.current) {
        const { data: cur } = await api.get(`/research/missions/${created.mission_id}`);
        if (seq !== searchSeq.current) return;
        m = cur; setMission(cur);
        if (["done", "error", "cancelled"].includes(cur.status)) break;
        await new Promise((r) => setTimeout(r, 700));
      }
      if (!m || seq !== searchSeq.current) return;
      if (m.status === "cancelled") { toast("Recherche annulée"); return; }
      if (m.status === "error" || !m.result) { toast.error("La recherche a échoué. Réessayez dans un instant."); return; }
      const data = m.result;
      const c = data.criteria || {};
      const hasLoc = !!c.location;
      const r = Number(c.radius_km) || 30;
      set({ result: data, region: "all",
            maxKm: hasLoc ? Math.min(DIST_MAX, Math.max(10, r)) : "all" });
      // RÈGLE FRANCE-ONLY (section 25) : localisation étrangère demandée -> message dédié.
      if (data.market_notice) toast.info(data.market_notice, { duration: 6000 });
    } catch (e) {
      if (seq !== searchSeq.current) return;
      if (e.response?.status === 402) {
        toast.error("Abonnez-vous à interKO (29 €/mois, sans engagement) pour continuer.");
        setShowUpgrade(true);
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally {
      if (seq === searchSeq.current) setLoading(false);
    }
  };

  const cancelMission = async () => {
    const id = missionId.current;
    searchSeq.current += 1;
    setLoading(false);
    setMission(null);
    toast("Recherche annulée");
    if (id) { try { await api.post(`/research/missions/${id}/cancel`); } catch { /* déjà terminée */ } }
  };

  useEffect(() => () => { searchSeq.current += 1; }, []);

  // Le type d'opportunité + le TJM sont des filtres SERVEUR -> on relance la recherche.
  const changeOppType = (v) => { set({ oppType: v }); if (result) runSearch(query, { oppType: v }); };
  const applyTjm = () => { if (result) runSearch(query); };

  // Inclure télétravail/national -> le serveur décide de l'inclusion : on relance la recherche.
  const toggleRemote = () => {
    const next = !includeRemote;
    set({ includeRemote: next });
    if (result) runSearch(query, { includeRemote: next });
  };

  // Masquage PAR UTILISATEUR (persistant) avec Annuler immédiat.
  const hideSignal = async (item, reason) => {
    const id = item.opportunity.id;
    const prev = result;
    const next = {
      ...result,
      opportunities: (result.opportunities || []).filter((o) => o.opportunity.id !== id),
      location_unverified: (result.location_unverified || []).filter((o) => o.opportunity.id !== id),
    };
    set({ result: next });
    try {
      await api.post(`/opportunities/${id}/hide`, { reason });
      toast("Signal masqué", {
        action: {
          label: "Annuler",
          onClick: async () => {
            try { await api.delete(`/opportunities/${id}/hide`); set({ result: prev }); toast.success("Signal restauré"); }
            catch { toast.error("Impossible de restaurer le signal"); }
          },
        },
      });
    } catch (e) {
      set({ result: prev });
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // A4 — Ouverture depuis « Recherches sauvegardées » : relance la recherche et liste les signaux.
  useEffect(() => {
    const q = location.state?.runQuery;
    if (q) {
      navigate(pathname, { replace: true, state: null });
      runSearch(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.runQuery]);

  // Deep-link email « Voir les opportunités » → /app?saved=<id> : relance la recherche sauvegardée
  // avec SES filtres (type d'opportunité inclus) pour afficher EXACTEMENT les signaux de l'alerte.
  useEffect(() => {
    const savedId = new URLSearchParams(location.search).get("saved");
    if (!savedId) return;
    (async () => {
      try {
        const { data } = await api.get(`/saved-searches/${savedId}`);
        const ot = data.opportunity_type || "all";
        const tmin = data.tjm_min ?? "";
        const tmax = data.tjm_max ?? "";
        const tincl = data.tjm_include_unspecified ?? true;
        set({ oppType: ot, tjmMin: tmin, tjmMax: tmax, tjmInclUnspec: tincl });
        navigate(pathname, { replace: true, state: null });
        await runSearch(data.query || "", { oppType: ot, tjmMin: tmin, tjmMax: tmax, tjmInclUnspec: tincl });
      } catch {
        toast.error("Recherche sauvegardée introuvable.");
        navigate(pathname, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const pickRole = (r) => {
    const extra = (r.skills || []).slice(0, 3).join(", ");
    const add = r.name_fr + (extra ? `, ${extra}` : "");
    set({ query: query.trim() ? `${query.trim()}, ${add}` : add });
  };

  const saveSearch = async () => {
    try {
      await api.post("/saved-searches", {
        query, criteria: result?.criteria || {},
        opportunity_type: oppType,
        tjm_min: tjmMin === "" ? null : Number(tjmMin),
        tjm_max: tjmMax === "" ? null : Number(tjmMax),
        tjm_include_unspecified: tjmInclUnspec,
      });
      toast.success("Recherche sauvegardée (filtre type + TJM inclus)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Masquer une ENTREPRISE pour toute l'organisation (réversible, propre au client).
  const hideCompany = async (item) => {
    const cid = item.company?.id;
    const cname = item.company?.name || "cette entreprise";
    if (!cid) return;
    const prev = result;
    const next = {
      ...result,
      opportunities: (result.opportunities || []).filter((o) => o.company?.id !== cid),
      location_unverified: (result.location_unverified || []).filter((o) => o.company?.id !== cid),
    };
    set({ result: next });
    try {
      await api.post(`/companies/${cid}/hide`, {});
      toast(`${cname} masquée pour votre organisation`, {
        action: {
          label: "Annuler",
          onClick: async () => {
            try { await api.delete(`/companies/${cid}/hide`); set({ result: prev }); toast.success("Entreprise restaurée"); }
            catch { toast.error("Impossible de restaurer l'entreprise"); }
          },
        },
      });
    } catch (e) {
      set({ result: prev });
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const crit = result?.criteria;
  const regions = result
    ? Array.from(new Set(result.opportunities.map((o) => o.opportunity?.region).filter(Boolean))).sort()
    : [];
  const hasLocation = !!crit?.location;
  const withinDistance = (o) => {
    if (maxKm === "all") return true;
    if (o.distance_km == null) return true;
    return o.distance_km <= Number(maxKm);
  };
  const applyFilters = (arr) => (arr || []).filter((o) =>
    o.score >= Number(minScore)
    && (!hideInter || o.opportunity?.client_type !== "intermediary")
    && (region === "all" || o.opportunity?.region === region)
    && (seniority === "all" || o.opportunity?.seniority === seniority)
    && withinDistance(o));
  const sortItems = (arr) => {
    const a = [...arr];
    if (sortBy === "relevance") a.sort((x, y) => y.score - x.score);
    else a.sort((x, y) => new Date(y.posted_date || y.opportunity?.posted_at || 0) - new Date(x.posted_date || x.opportunity?.posted_at || 0));
    return a;
  };
  const baseFiltered = result ? applyFilters(result.opportunities) : [];
  const relatedCount = baseFiltered.filter((o) => o.match_category === "related").length;
  const filtered = sortItems(relatedOnly ? baseFiltered.filter((o) => o.match_category === "related") : baseFiltered);
  const unverified = result ? sortItems(applyFilters(result.location_unverified)) : [];

  return (
    <div>
      {/* Search hero */}
      <div className="mb-8">
        <div className="overline mb-2">Recherche d'opportunités</div>
        <h1 className="font-display text-3xl font-bold mb-1">Quel type de mission recherchez-vous pour votre consultant ?</h1>
        <p className="text-muted text-sm mb-6">Décrivez le profil de votre consultant, ses compétences, sa séniorité et sa zone de mobilité.</p>

        <div className={`surface rounded-xl p-2 relative overflow-hidden ${loading ? "scanning" : ""}`}>
          <div className="flex items-start gap-3 bg-surface-2 rounded-lg px-4 py-3 border border-border">
            <MagnifyingGlass size={22} className="text-accent shrink-0 mt-1.5" />
            <textarea
              value={query}
              onChange={(e) => set({ query: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); runSearch(); } }}
              rows={2}
              data-testid="search-input"
              placeholder="Ex : Business Analyst autour de Lille dans un rayon de 30 km. (Entrée pour lancer, Maj+Entrée = nouvelle ligne)"
              className="flex-1 bg-transparent text-fg text-sm sm:text-base resize-none focus:outline-none py-1 font-sans"
            />
            <button
              onClick={() => runSearch()}
              disabled={loading}
              data-testid="search-submit"
              className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift flex items-center gap-2 shrink-0 disabled:opacity-60"
            >
              <Sparkle size={16} weight="fill" /> {loading ? "Recherche IA…" : "Recherche IA"}
            </button>
          </div>
        </div>

        {!result && !loading && <RoleAutocomplete onPick={pickRole} />}

        {!result && !loading && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="search-examples">
            <span className="text-xs text-muted mr-1 mt-1.5">Exemples :</span>
            {EXAMPLES.map((ex, i) => (
              <button key={i} onClick={() => runSearch(ex)}
                className="text-xs bg-surface border border-border rounded-full px-3 py-1.5 text-muted hover:text-fg hover:border-accent/40 hover-lift">
                {ex.length > 46 ? ex.slice(0, 46) + "…" : ex}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <MissionProgress mission={mission} onCancel={cancelMission} />}

      {loading && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="search-loading">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="surface rounded-lg p-5 h-44 animate-pulse" />
          ))}
        </div>
      )}

      {/* Criteria + results */}
      {result && !loading && (
        <>
          <div className="surface rounded-lg p-5 mb-6" data-testid="criteria-panel">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="overline mb-2 flex items-center gap-2"><Sparkle size={13} className="text-accent" weight="fill" /> Critères extraits par l'IA</div>
                <p className="text-sm text-fg/90">{crit.summary}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(crit.skills || []).map((s) => (
                    <span key={s} className="text-xs font-mono bg-accent/15 text-accent border border-accent/30 px-2 py-0.5 rounded">{s}</span>
                  ))}
                  <span className="text-xs font-mono bg-surface-2 text-muted border border-border px-2 py-0.5 rounded">Séniorité : {crit.seniority}</span>
                  {crit.canonical_role?.name && (
                    <span className="text-xs font-mono bg-accent/10 text-accent border border-accent/30 px-2 py-0.5 rounded flex items-center gap-1" data-testid="criteria-role-chip">
                      <Stack size={11} /> {crit.canonical_role.name} · {crit.canonical_role.family_name}
                    </span>
                  )}
                  {crit.location && <span className="text-xs font-mono bg-surface-2 text-muted border border-border px-2 py-0.5 rounded">{crit.location} · {crit.radius_km} km</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={saveSearch} data-testid="save-search-btn"
                  className="flex items-center gap-2 text-sm border border-border rounded-md px-4 py-2.5 hover-lift hover:border-accent/40 text-fg">
                  <BookmarkSimple size={16} /> Sauvegarder
                </button>
                <button onClick={() => reset()} data-testid="clear-search-btn"
                  className="flex items-center gap-2 text-sm border border-border rounded-md px-4 py-2.5 hover-lift hover:border-accent/40 text-muted hover:text-fg">
                  <X size={16} /> Nouvelle recherche
                </button>
                <button onClick={() => navigate("/app/hidden-signals")} data-testid="open-hidden-signals-btn"
                  title="Consulter et restaurer vos signaux masqués"
                  className="flex items-center gap-2 text-sm border border-border rounded-md px-4 py-2.5 hover-lift hover:border-accent/40 text-muted hover:text-fg">
                  <EyeSlash size={16} /> Masqués
                </button>
                <button onClick={() => navigate("/app/hidden-companies")} data-testid="open-hidden-companies-btn"
                  title="Consulter et restaurer les entreprises masquées pour votre organisation"
                  className="flex items-center gap-2 text-sm border border-border rounded-md px-4 py-2.5 hover-lift hover:border-accent/40 text-muted hover:text-fg">
                  <Buildings size={16} /> Entreprises masquées
                </button>
              </div>
            </div>
          </div>

          {/* Type d'opportunité + TJM (filtres serveur) */}
          <div className="surface rounded-lg p-5 mb-4" data-testid="opportunity-filters">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
              <div>
                <div className="overline mb-2 flex items-center gap-2"><Target size={13} className="text-accent" /> Type d'opportunité</div>
                <div className="flex items-center gap-1 bg-surface border border-border rounded-md p-1 w-fit" data-testid="opp-type-toggle">
                  {OPP_TYPES.map((t) => {
                    const Icon = t.icon;
                    const cnt = result.category_counts ? (result.category_counts[t.v] ?? 0) : null;
                    return (
                      <button key={t.v} onClick={() => changeOppType(t.v)} data-testid={`opp-type-${t.v}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium ${oppType === t.v ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"}`}>
                        <Icon size={14} /> {t.label}
                        {cnt != null && <span className={`font-mono ${oppType === t.v ? "text-accent" : "text-muted/70"}`}>· {cnt}</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted mt-2 max-w-md">Par défaut : clients finaux à prospecter en direct. Les opportunités via ESN restent accessibles (sous-traitance / partenariat).</p>
              </div>
              <div>
                <div className="overline mb-2 flex items-center gap-2"><Coins size={13} className="text-accent" /> Filtre TJM (tarif journalier)</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="number" min="0" value={tjmMin} onChange={(e) => set({ tjmMin: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && applyTjm()} placeholder="TJM min €"
                    data-testid="tjm-min-input"
                    className="w-28 bg-surface-2 border border-border rounded-md px-3 py-1.5 text-sm text-fg focus:outline-none focus:border-accent/40" />
                  <span className="text-muted text-xs">—</span>
                  <input type="number" min="0" value={tjmMax} onChange={(e) => set({ tjmMax: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && applyTjm()} placeholder="TJM max €"
                    data-testid="tjm-max-input"
                    className="w-28 bg-surface-2 border border-border rounded-md px-3 py-1.5 text-sm text-fg focus:outline-none focus:border-accent/40" />
                  <button onClick={applyTjm} data-testid="tjm-apply-btn"
                    className="text-xs bg-accent/15 text-accent border border-accent/30 rounded-md px-3 py-1.5 hover:bg-accent/25">Appliquer</button>
                  {(tjmMin || tjmMax) && (
                    <button onClick={() => { set({ tjmMin: "", tjmMax: "" }); if (result) runSearch(query, { tjmMin: "", tjmMax: "" }); }}
                      data-testid="tjm-clear-btn" className="text-xs text-muted hover:text-fg border border-border rounded-md px-2 py-1.5">Effacer</button>
                  )}
                </div>
                <label className="flex items-center gap-2 mt-2 text-[11px] text-muted cursor-pointer" data-testid="tjm-include-unspecified">
                  <input type="checkbox" checked={tjmInclUnspec && !(tjmMin || tjmMax)} disabled={!!(tjmMin || tjmMax)}
                    onChange={(e) => { set({ tjmInclUnspec: e.target.checked }); }} className="accent-[#00E5FF]" />
                  Inclure les opportunités sans TJM communiqué
                </label>
                {(tjmMin || tjmMax) && (
                  <p className="text-[11px] text-amber-400/90 mt-1">Beaucoup d'offres classiques n'affichent pas de TJM : elles sont exclues quand ce filtre est actif.</p>
                )}
              </div>
            </div>
          </div>

          {hasLocation && (
            <DistanceGauge value={maxKm} includeRemote={includeRemote}
              onChange={(v) => set({ maxKm: v })}
              onToggleRemote={toggleRemote} />
          )}

          <div className="flex items-center gap-4 mb-4 text-sm flex-wrap">
            <div className="flex items-center gap-2 text-fg"><Target size={16} className="text-accent" /> <span className="font-mono font-bold">{filtered.length}</span> opportunité{filtered.length > 1 ? "s" : ""}{minScore !== "0" ? ` (score ≥ ${minScore})` : " pertinentes"}</div>
            {result.min_relevance != null && (
              <span className="flex items-center gap-1.5 text-xs font-mono text-accent/90 bg-accent/10 border border-accent/25 rounded-full px-2.5 py-1"
                data-testid="relevance-threshold-badge" title={`Seuls les signaux réellement pertinents (pertinence IA ≥ ${result.min_relevance}%) sont affichés. Les métiers/technos sans rapport avec le profil sont filtrés.`}>
                <Sparkle size={12} weight="fill" /> Pertinence ≥ {result.min_relevance}%
              </span>
            )}
            {relatedCount > 0 && (
              <button onClick={() => set({ relatedOnly: !relatedOnly })} data-testid="related-count-badge"
                title="Opportunités sur des métiers proches de votre recherche (profils adjacents à positionner). Cliquez pour n'afficher qu'eux."
                className={`flex items-center gap-1.5 text-xs font-mono rounded-full px-2.5 py-1 border ${relatedOnly ? "bg-[#7C5CFF]/25 border-[#7C5CFF] text-white" : "bg-[#7C5CFF]/10 border-[#7C5CFF]/30 text-[#b9a6ff] hover:border-[#7C5CFF]/60"}`}>
                <Target size={12} weight="fill" /> {relatedCount} via métiers proches
              </button>
            )}
            <div className="ml-auto flex items-center gap-3 flex-wrap">
              <button onClick={() => set({ hideInter: !hideInter })} data-testid="hide-intermediaries-toggle"
                className={`flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs font-medium ${hideInter ? "bg-accent/12 border-accent/40 text-accent" : "bg-surface border-border text-muted hover:text-fg"}`}>
                <ShieldWarning size={14} /> Masquer les intermédiaires
              </button>
              {regions.length > 1 && (
                <div className="flex items-center gap-2" data-testid="region-filter">
                  <MapPin size={15} className="text-muted" />
                  <Select value={region} onValueChange={(v) => set({ region: v })}>
                    <SelectTrigger className="h-9 w-48 bg-surface border-border text-xs" data-testid="region-filter-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="region-filter-all">Toutes les régions</SelectItem>
                      {regions.map((r) => <SelectItem key={r} value={r} data-testid={`region-filter-${r}`}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {hasLocation && maxKm !== "all" && (
                <span className="flex items-center gap-1.5 text-xs font-mono text-muted bg-surface border border-border rounded-full px-2.5 py-1" data-testid="active-radius-chip">
                  <MapPin size={12} className="text-accent" /> ≤ {maxKm} km de {crit.location}
                </span>
              )}
              <div className="flex items-center bg-surface border border-border rounded-md p-1" data-testid="sort-toggle">
                <button onClick={() => set({ sortBy: "recent" })} data-testid="sort-recent-btn"
                  title="Trier du plus récent au plus ancien (date de publication réelle)"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium ${sortBy === "recent" ? "bg-surface-2 text-fg" : "text-muted hover:text-fg"}`}>
                  <Clock size={15} /> Récent
                </button>
                <button onClick={() => set({ sortBy: "relevance" })} data-testid="sort-relevance-btn"
                  title="Trier par pertinence (score de match)"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium ${sortBy === "relevance" ? "bg-surface-2 text-fg" : "text-muted hover:text-fg"}`}>
                  <Target size={15} /> Pertinent
                </button>
              </div>
              <div className="flex items-center gap-2" data-testid="score-filter">
                <Funnel size={15} className="text-muted" />
                <Select value={minScore} onValueChange={(v) => set({ minScore: v })}>
                  <SelectTrigger className="h-9 w-40 bg-surface border-border text-xs" data-testid="score-filter-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SCORE_FILTERS.map((f) => <SelectItem key={f.v} value={f.v} data-testid={`score-filter-${f.v}`}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2" data-testid="seniority-filter">
                <Stack size={15} className="text-muted" />
                <Select value={seniority} onValueChange={(v) => set({ seniority: v })}>
                  <SelectTrigger className="h-9 w-40 bg-surface border-border text-xs" data-testid="seniority-filter-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SENIORITY_FILTERS.map((f) => <SelectItem key={f.v} value={f.v} data-testid={`seniority-filter-${f.v}`}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center bg-surface border border-border rounded-md p-1" data-testid="view-toggle">
                <button onClick={() => set({ view: "list" })} data-testid="view-list-btn"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium ${view === "list" ? "bg-surface-2 text-fg" : "text-muted hover:text-fg"}`}>
                  <ListBullets size={15} /> Liste
                </button>
                <button onClick={() => set({ view: "map" })} data-testid="view-map-btn"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium ${view === "map" ? "bg-surface-2 text-fg" : "text-muted hover:text-fg"}`}>
                  <MapTrifold size={15} /> Carte
                </button>
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="surface rounded-lg p-10 text-center text-muted" data-testid="no-results">
              {result.opportunities.length === 0
                ? "Aucune opportunité pertinente détectée pour ce profil. Essayez d'autres compétences ou une autre formulation."
                : "Aucune opportunité ne correspond à ces filtres. Élargissez la région, la distance ou le seuil de score."}
            </div>
          ) : view === "list" ? (
            oppType === "all" && filtered.some((o) => o.company_class === "FINAL_CLIENT") && filtered.some((o) => o.company_class !== "FINAL_CLIENT") ? (
              <div className="space-y-8" data-testid="opportunities-sections">
                <div data-testid="section-final-clients">
                  <div className="flex items-center gap-2 mb-3">
                    <Buildings size={16} className="text-[#22C55E]" />
                    <h3 className="font-display font-bold text-fg">Clients finaux à prospecter</h3>
                    <span className="text-xs font-mono text-muted bg-surface border border-border rounded-full px-2 py-0.5">{filtered.filter((o) => o.company_class === "FINAL_CLIENT").length}</span>
                  </div>
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.filter((o) => o.company_class === "FINAL_CLIENT").map((item, i) => (
                      <OpportunityCard key={item.opportunity.id} item={item} index={i} onHide={hideSignal} onHideCompany={hideCompany} />
                    ))}
                  </div>
                </div>
                <div data-testid="section-indirect">
                  <div className="flex items-center gap-2 mb-3">
                    <Handshake size={16} className="text-[#F59E0B]" />
                    <h3 className="font-display font-bold text-fg">Opportunités indirectes via ESN ou intermédiaires</h3>
                    <span className="text-xs font-mono text-muted bg-surface border border-border rounded-full px-2 py-0.5">{filtered.filter((o) => o.company_class !== "FINAL_CLIENT").length}</span>
                  </div>
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.filter((o) => o.company_class !== "FINAL_CLIENT").map((item, i) => (
                      <OpportunityCard key={item.opportunity.id} item={item} index={i} onHide={hideSignal} onHideCompany={hideCompany} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="opportunities-grid">
                {filtered.map((item, i) => (
                  <OpportunityCard key={item.opportunity.id} item={item} index={i} onHide={hideSignal} onHideCompany={hideCompany} />
                ))}
              </div>
            )
          ) : (
            <OpportunityMap opportunities={filtered} />
          )}

          {unverified.length > 0 && (
            <div className="mt-8" data-testid="location-unverified-section">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="text-amber-400" />
                <h3 className="font-display font-bold text-fg">Localisation à vérifier</h3>
                <span className="text-xs font-mono text-muted bg-surface border border-border rounded-full px-2 py-0.5">{unverified.length}</span>
              </div>
              <p className="text-xs text-muted mb-4 max-w-2xl">
                Ces annonces sont pertinentes mais leur lieu de travail est national ou 100 % télétravail.
              </p>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 opacity-90" data-testid="unverified-grid">
                {unverified.map((item, i) => (
                  <OpportunityCard key={item.opportunity.id} item={item} index={i} onHide={hideSignal} onHideCompany={hideCompany} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!result && !loading && (
        <div className="grid sm:grid-cols-3 gap-4 mt-10" data-testid="dashboard-empty">
          {[
            { icon: Broadcast, t: "Signaux du marché", d: "Les besoins de recrutement deviennent des opportunités." },
            { icon: Buildings, t: "Entreprises ciblées", d: "Les sociétés qui recherchent vos compétences." },
            { icon: Target, t: "Score d'opportunité", d: "Priorisez les meilleures correspondances." },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="surface rounded-lg p-5">
                <Icon size={22} weight="duotone" className="text-accent mb-3" />
                <div className="font-display font-bold text-fg mb-1">{c.t}</div>
                <div className="text-sm text-muted">{c.d}</div>
              </div>
            );
          })}
        </div>
      )}

      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} returnTo={pathname}
        reason="Abonnez-vous à interKO (29 €/mois, sans engagement) pour détecter des opportunités et contacter les bons décideurs." />
    </div>
  );
}
